import React, { useMemo, useState } from "react";
import type { EnrichedRule } from "../api/sdhAPI";
import { flagsOf, HIDDEN_FLAGS, pendingProposal } from "./RuleLogicView";

/**
 * Role views of the rule set (one data source, the rules the page already loads):
 *   LifecycleBar   - entity-level status, the same numbers on every view
 *   LeadershipView - where are we, what did the rules find, which fields are covered
 *   TechnicalView  - what runs, is it healthy, the executed logic of every rule
 * Every count uses lifeOf(), so a number means the same thing everywhere.
 */
export type Life = "approved" | "pending" | "rejected" | "preapproved" | "review" | "hidden";

export const LIFE: Record<Life, { label: string; tone: string; hint: string }> = {
  approved: { label: "Approved", tone: "#2f7a4f", hint: "approved by the SME" },
  preapproved: { label: "Pre-approved", tone: "#4a9b72", hint: "pre-approved on evidence, to confirm" },
  review: { label: "Needs a decision", tone: "#43607e", hint: "not reviewed yet" },
  pending: { label: "Pending business", tone: "#c98a1b", hint: "waiting for business approval" },
  rejected: { label: "Rejected", tone: "#c0392b", hint: "rejected by the SME or merged into another rule" },
  hidden: { label: "Set aside by the agent", tone: "#8a97a3", hint: "kept out of your queue, each with a stated reason (contradicted by the data, cannot fail, duplicate, too few rows, integrity check) - open them only if you disagree" },
};
const ORDER: Life[] = ["approved", "preapproved", "review", "pending", "rejected"];

const raw = (r: any) => r?._raw || {};
export const impactOf = (r: any) => raw(r).impact_assessment || {};
const gateOf = (r: any) => String((r.sme_decision || raw(r).sme_decision || {}).decision_gate || "").toUpperCase();
const ownerOf = (r: any) => String(r.owner_field || raw(r).owner_field_id || "::");

export function lifeOf(r: EnrichedRule): Life {
  const lc = String((r as any).lifecycle || raw(r).lifecycle || "").toLowerCase();
  const g = gateOf(r);
  if (lc === "rejected" || g === "REJECTED") return "rejected";
  if (g === "APPROVED" || lc === "active" || lc === "approved") return "approved";
  if (g === "PENDING" || g === "CONDITIONAL") return "pending";
  if (flagsOf(r).some((f) => HIDDEN_FLAGS.has(f))) return "hidden";
  const disp = String((r as any).disposition || raw(r).disposition || "");
  return disp === "AUTO_EXECUTE" ? "preapproved" : "review";
}

const fmt = (n: any) => (n == null || isNaN(Number(n)) ? "—" : Number(n).toLocaleString());

/* ───────────── lifecycle bar (entity level) ───────────── */
export function LifecycleBar({ rules, title }: { rules: EnrichedRule[]; title?: string }) {
  const c = useMemo(() => {
    const m: Record<Life, number> = { approved: 0, preapproved: 0, review: 0, pending: 0, rejected: 0, hidden: 0 };
    rules.forEach((r) => { m[lifeOf(r)] += 1; });
    return m;
  }, [rules]);
  const inScope = ORDER.reduce((a, k) => a + (k === "rejected" ? 0 : c[k]), 0);
  const total = ORDER.reduce((a, k) => a + c[k], 0);
  const proposals = rules.filter((r) => pendingProposal(r)).length;
  const done = c.approved;
  return (
    <div className="rv-life">
      <div className="rv-life-h">
        <b>{title || "Review status"}</b>
        <span className="mut">{fmt(inScope)} rules in review · {total ? Math.round((100 * done) / Math.max(1, inScope)) : 0}% approved
          {proposals ? ` · ${proposals} proposal(s) waiting` : ""}{c.hidden ? ` · ${c.hidden} set aside by the agent` : ""}</span>
      </div>
      <div className="rv-bar">
        {ORDER.map((k) => c[k] ? <i key={k} style={{ flex: c[k], background: LIFE[k].tone }} title={`${LIFE[k].label}: ${c[k]}`} /> : null)}
      </div>
      <div className="rv-legend">
        {ORDER.concat(["hidden"] as Life[]).map((k) => (
          <span key={k} title={LIFE[k].hint}><i style={{ background: LIFE[k].tone }} /> {LIFE[k].label} <b>{c[k]}</b></span>
        ))}
      </div>
    </div>
  );
}

/* ───────────── SME action queue (review tab) ───────────── */
export type Bucket = "review" | "preapproved" | "proposal" | "hidden" | "done" | null;
const REASON: Record<string, string> = { contradicted: "contradicted by the data", cannot_fail: "cannot fail", equivalent_to: "duplicate",
  implied_by: "covered by a broader rule", insufficient_sample: "too few rows", integrity_check: "integrity check",
  rejected_by_data: "rejected by the data", not_tested: "not testable", merged_into: "merged" };
export function matchBucket(r: EnrichedRule, b: Bucket): boolean {
  if (!b) return true;
  const l = lifeOf(r);
  if (b === "proposal") return !!pendingProposal(r);
  if (b === "done") return l === "approved" || l === "rejected" || l === "pending";
  return l === b;
}
export function ActionQueue({ rules, active, onPick, setAside, saCat, onPickCat }: { rules: EnrichedRule[]; active: Bucket; onPick: (b: Bucket) => void;
  setAside?: Record<string, { label: string; action: string; count: number }>; saCat?: string | null; onPickCat?: (c: string | null) => void }) {
  const n = (b: Bucket) => rules.filter((r) => matchBucket(r, b)).length;
  const reasons = useMemo(() => {
    const t: Record<string, number> = {};
    rules.filter((r) => lifeOf(r) === "hidden").forEach((r) => {
      const f = flagsOf(r).find((x) => HIDDEN_FLAGS.has(x)); if (f) t[f] = (t[f] || 0) + 1; });
    return t;
  }, [rules]);
  const cards: Array<[Bucket, string, string, string]> = [
    [null, "All", "every rule — pick a status to filter the tables, fields and rules below", "#1d2b36"],
    ["review", "Decide", "rules waiting for your decision", "#43607e"],
    ["preapproved", "Confirm", "pre-approved on evidence — confirm or refine", "#4a9b72"],
    ["proposal", "Proposals", "a newer version waits for you — compare and activate", "#c98a1b"],
    ["hidden", "Set aside", "kept out of your queue by the agent, with a reason", "#8a97a3"],
    ["done", "Done", "approved, rejected or with the business", "#2f7a4f"]];
  return (
    <div className="rv-q2">
      {cards.map(([b, t, h, tone]) => (
        <button key={String(b)} className={"rv-qc" + (active === b ? " on" : "")} style={{ ["--t" as any]: tone }}
          onClick={() => onPick(b === null ? null : (active === b ? null : b))} title={h}>
          <b>{b === null ? rules.length : n(b)}</b><span className="t">{t}</span><span className="h">{h}</span>
          {b === "hidden" && setAside && Object.values(setAside).some((x) => x.count) ? <span className="rs">
            {Object.entries(setAside).filter(([, x]) => x.count).map(([k, x]) => <i key={k} className={"sa " + k + (saCat === k ? " on" : "")} role="button"
              title={`${x.action} — click to show only these`}
              onClick={(e) => { e.stopPropagation(); onPickCat?.(saCat === k ? null : k); }}>{x.count} {x.label}</i>)}</span>
          : b === "hidden" && Object.keys(reasons).length > 0 && <span className="rs">
            {Object.entries(reasons).map(([f, k]) => <i key={f}>{k} {REASON[f] || f}</i>)}</span>}
        </button>))}
    </div>
  );
}

/* ───────────── per-table / per-field status breakdown (when "All" is selected) ───────────── */
const TO_BUCKET: Record<Life, Bucket> = { review: "review", preapproved: "preapproved", hidden: "hidden",
  approved: "done", pending: "done", rejected: "done" };
export function StatusMini({ rules, onPick }: { rules: EnrichedRule[]; onPick?: (b: Bucket) => void }) {
  if (!rules.length) return null;
  const c: Record<Life, number> = { approved: 0, preapproved: 0, review: 0, pending: 0, rejected: 0, hidden: 0 };
  rules.forEach((r) => { c[lifeOf(r)] += 1; });
  const order: Life[] = ["review", "preapproved", "approved", "pending", "rejected", "hidden"];
  const done = c.approved + c.pending + c.rejected;
  const pick = (b: Bucket) => (e: React.MouseEvent) => { e.stopPropagation(); onPick?.(b); };
  return (
    <span className="rv-mini">
      <span className="bar">{order.map((k) => c[k] ? <i key={k} style={{ flex: c[k], background: LIFE[k].tone }}
        title={`${LIFE[k].label}: ${c[k]} — click to show only these`} onClick={pick(TO_BUCKET[k])} /> : null)}</span>
      <span className="nums">
        {c.review ? <em onClick={pick("review")}>{c.review} decide</em> : null}
        {c.preapproved ? <em onClick={pick("preapproved")}>{c.preapproved} confirm</em> : null}
        {done ? <em onClick={pick("done")}>{done} done</em> : null}
        {c.hidden ? <em className="sa" onClick={pick("hidden")}>{c.hidden} set aside</em> : null}
      </span>
    </span>
  );
}

/* ───────────── leadership view ───────────── */
export function LeadershipView({ rules, onOpen }: { rules: EnrichedRule[]; onOpen?: (r: EnrichedRule) => void }) {
  const live = (r: EnrichedRule) => ["approved", "preapproved"].includes(lifeOf(r));
  const stats = useMemo(() => {
    const byTable: Record<string, Record<Life, number>> = {};
    const byField: Record<string, { field: string; rules: number; approved: number; open: number; defects: number }> = {};
    let defects = 0, covered = 0;
    rules.forEach((r) => {
      const [t, col] = ownerOf(r).split("::");
      const l = lifeOf(r);
      byTable[t] = byTable[t] || { approved: 0, preapproved: 0, review: 0, pending: 0, rejected: 0, hidden: 0 };
      byTable[t][l] += 1;
      const f = byField[`${t}::${col}`] = byField[`${t}::${col}`] || { field: `${t}.${col}`, rules: 0, approved: 0, open: 0, defects: 0 };
      if (l !== "hidden" && l !== "rejected") f.rules += 1;
      if (l === "approved") f.approved += 1;
      if (l === "review" || l === "preapproved" || l === "pending") f.open += 1;
      const ia = impactOf(r);
      if (live(r) && ia.executed) { f.defects += Number(ia.violations || 0); defects += Number(ia.violations || 0); }
    });
    Object.values(byField).forEach((f) => { if (f.approved) covered += 1; });
    const top = rules.filter((r) => live(r) && impactOf(r).executed && Number(impactOf(r).violations) > 0)
      .sort((a, b) => Number(impactOf(b).violations) - Number(impactOf(a).violations)).slice(0, 10);
    return { byTable, fields: Object.values(byField).sort((a, b) => b.defects - a.defects), defects, covered, top };
  }, [rules]);
  const inScope = rules.filter((r) => !["hidden", "rejected"].includes(lifeOf(r))).length;
  const approved = rules.filter((r) => lifeOf(r) === "approved").length;
  return (
    <div className="rv">
      <div className="rv-kpis">
        <div><b>{fmt(inScope)}</b><span>rules in review scope</span></div>
        <div><b>{inScope ? Math.round((100 * approved) / inScope) : 0}%</b><span>approved ({fmt(approved)})</span></div>
        <div><b>{fmt(stats.defects)}</b><span>records flagged by approved / pre-approved rules — data issues found</span></div>
        <div><b>{fmt(stats.covered)} / {fmt(stats.fields.length)}</b><span>fields with at least one approved rule</span></div>
      </div>
      <LifecycleBar rules={rules} title="Review status — whole entity" />
      <div className="rv-two">
        <section className="rv-card">
          <h4>By table</h4>
          {Object.entries(stats.byTable).map(([t, m]) => {
            const n = ORDER.reduce((a, k) => a + m[k], 0);
            return <div key={t} className="rv-row"><span className="rv-k">{t}</span>
              <div className="rv-bar sm">{ORDER.map((k) => m[k] ? <i key={k} style={{ flex: m[k], background: LIFE[k].tone }} title={`${LIFE[k].label}: ${m[k]}`} /> : null)}</div>
              <span className="mut">{n - m.rejected} in review: {m.review} to decide · {m.preapproved} to confirm · {m.approved} approved{m.hidden ? ` · ${m.hidden} set aside` : ""}</span></div>;
          })}
        </section>
        <section className="rv-card">
          <h4>Where the rules find the most data issues</h4>
          {stats.top.length === 0 ? <p className="mut">No approved or pre-approved rule flags data yet.</p> :
            stats.top.map((r) => (
              <button key={r.rule_id} className="rv-top" onClick={() => onOpen?.(r)}>
                <span className="t">{r.rule_name || r.rule_id}</span>
                <span className="mut">{ownerOf(r).replace("::", ".")}</span>
                <b className="bad">{fmt(impactOf(r).violations)}</b>
              </button>))}
        </section>
      </div>
      <section className="rv-card">
        <h4>Fields</h4>
        <table className="rv-tbl"><thead><tr><th>Field</th><th>Rules in review</th><th>Approved</th><th>Open decisions</th><th>Records flagged</th></tr></thead>
          <tbody>{stats.fields.map((f) => <tr key={f.field}><td className="mono">{f.field}</td><td>{f.rules}</td>
            <td className={f.approved ? "ok" : "mut"}>{f.approved}</td><td>{f.open}</td><td className={f.defects ? "bad" : "mut"}>{fmt(f.defects)}</td></tr>)}</tbody></table>
      </section>
    </div>
  );
}

/* ───────────── technical view ───────────── */
export function TechnicalView({ rules, onOpen }: { rules: EnrichedRule[]; onOpen?: (r: EnrichedRule) => void }) {
  const [q, setQ] = useState("");
  const [only, setOnly] = useState<"" | "notexec" | "flags" | "proposal">("");
  const h = useMemo(() => {
    let exec = 0, notExec = 0, unmeasured = 0, flagged = 0, proposals = 0, versioned = 0;
    rules.forEach((r) => {
      const ia = impactOf(r);
      if (ia.executed) exec += 1; else if (ia.status === "not_executable" || ia.error) notExec += 1; else unmeasured += 1;
      if (ia.executed && Number(ia.violations) > 0) flagged += 1;
      if (pendingProposal(r)) proposals += 1;
      if (Number((r as any).version || 1) > 1) versioned += 1;
    });
    return { exec, notExec, unmeasured, flagged, proposals, versioned };
  }, [rules]);
  const shown = rules.filter((r) => {
    const ia = impactOf(r);
    if (only === "notexec" && (ia.executed || !(ia.status === "not_executable" || ia.error))) return false;
    if (only === "flags" && !(ia.executed && Number(ia.violations) > 0)) return false;
    if (only === "proposal" && !pendingProposal(r)) return false;
    if (!q) return true;
    return `${r.rule_id} ${r.rule_name} ${ownerOf(r)} ${ia.executed_rule || ""}`.toLowerCase().includes(q.toLowerCase());
  });
  return (
    <div className="rv">
      <div className="rv-kpis">
        <button className={only === "" ? "on" : ""} onClick={() => setOnly("")}><b>{fmt(rules.length)}</b><span>rules</span></button>
        <div><b className="ok">{fmt(h.exec)}</b><span>executed on the sample</span></div>
        <button className={only === "notexec" ? "on" : ""} onClick={() => setOnly("notexec")}><b className={h.notExec ? "bad" : ""}>{fmt(h.notExec)}</b><span>not executable</span></button>
        <div><b>{fmt(h.unmeasured)}</b><span>not measured yet</span></div>
        <button className={only === "flags" ? "on" : ""} onClick={() => setOnly("flags")}><b>{fmt(h.flagged)}</b><span>flag data</span></button>
        <button className={only === "proposal" ? "on" : ""} onClick={() => setOnly("proposal")}><b>{fmt(h.proposals)}</b><span>proposals waiting · {fmt(h.versioned)} on v2+</span></button>
      </div>
      <input className="rv-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search id, name, field or logic…" />
      <table className="rv-tbl tech"><thead><tr><th>Rule</th><th>Owner</th><th>Status</th><th>Result</th><th>v</th><th>Executes</th></tr></thead>
        <tbody>{shown.map((r) => { const ia = impactOf(r); const l = lifeOf(r);
          return <tr key={r.rule_id} onClick={() => onOpen?.(r)} className="click">
            <td><div>{r.rule_name || r.rule_id}</div><div className="mono mut">{r.rule_id}</div></td>
            <td className="mono">{ownerOf(r).replace("::", ".")}</td>
            <td><span className="rv-pill" style={{ background: LIFE[l].tone }}>{LIFE[l].label}</span>
              {flagsOf(r).length ? <div className="mut sm">{flagsOf(r).join(", ")}</div> : null}</td>
            <td className={ia.executed ? (Number(ia.violations) ? "bad" : "ok") : "mut"}>
              {ia.executed ? `${fmt(ia.violations)} / ${fmt(ia.applicable_rows)}` : (ia.status || "not measured")}</td>
            <td>{(r as any).version || 1}{pendingProposal(r) ? "+" : ""}</td>
            <td className="mono sm">{ia.executed_rule || "—"}</td></tr>; })}</tbody></table>
    </div>
  );
}

export const ROLE_CSS = `
.rv-mini{display:flex;flex-direction:column;gap:3px;margin-top:5px;width:100%}
.rv-mini .bar{display:flex;height:6px;border-radius:3px;overflow:hidden;background:#eef1f4}.rv-mini .bar i{display:block;cursor:pointer}
.rv-mini .nums{display:flex;flex-wrap:wrap;gap:4px}.rv-mini em{font-style:normal;font-size:10.5px;background:#f1f3f5;border-radius:8px;padding:0 6px;cursor:pointer;color:#33424e}
.rv-mini em:hover{background:#efe3ff;color:#5a0091}.rv-mini em.sa{color:#6b7a86}
.rv-q2{display:grid;grid-template-columns:repeat(6,minmax(140px,1fr));gap:10px}@media(max-width:1100px){.rv-q2{grid-template-columns:repeat(2,1fr)}}
.rv-qc{background:#fff;border:1px solid #e3e8ee;border-left:4px solid var(--t);border-radius:10px;padding:10px 12px;text-align:left;cursor:pointer;font:inherit;display:flex;flex-direction:column;gap:2px}
.rv-qc .rs i.sa{cursor:pointer}.rv-qc .rs i.sa:hover{background:#efe6fb;color:#460073}.rv-qc .rs i.sa.on{background:#7500C0;color:#fff;border-color:#7500C0}
.rv-qc b{font-size:24px;color:var(--t)}.rv-qc .t{font-weight:700}.rv-qc .h{color:#6b7a86;font-size:11.5px;line-height:1.35}
.rv-qc.on{box-shadow:0 0 0 2px var(--t) inset;background:#fbf8ff}
/* COMPACT: one row of tiles (count + label); the description is hidden; set-aside categories only on the active tile */
.rv-q2 .rv-qc{flex-direction:row;flex-wrap:wrap;align-items:baseline;gap:2px 8px;padding:7px 12px;min-height:0}
.rv-q2 .rv-qc b{font-size:19px;line-height:1.1}.rv-q2 .rv-qc .t{font-size:13px}
.rv-q2 .rv-qc .h{display:block;flex-basis:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:11.5px}
.rv-q2 .rv-qc .rs{display:none;flex-basis:100%}.rv-q2 .rv-qc.on .rs{display:flex}.rv-qc .rs{display:flex;flex-wrap:wrap;gap:4px;margin-top:4px}.rv-qc .rs i{font-style:normal;font-size:11px;background:#f1f3f5;border-radius:8px;padding:1px 6px}
.rv-filter{display:flex;align-items:center;gap:10px;background:#f6f0ff;border:1px solid #d9c2f5;border-radius:8px;padding:6px 10px;margin:6px 0}
.rv-filter button{border:1px solid #cfd7de;background:#fff;border-radius:6px;padding:2px 8px;cursor:pointer}
.rv{display:flex;flex-direction:column;gap:12px}
.rv-kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:10px}
.rv-kpis>div,.rv-kpis>button{background:#fff;border:1px solid #e3e8ee;border-radius:10px;padding:10px 12px;text-align:left;font:inherit}
.rv-kpis>button{cursor:pointer}.rv-kpis>button.on{border-color:#7500C0;box-shadow:inset 0 3px 0 #A100FF}
.rv-kpis b{display:block;font-size:22px}.rv-kpis span{color:#5b6b78;font-size:12px}
.rv-life{background:#fff;border:1px solid #e3e8ee;border-radius:10px;padding:10px 12px}
.rv-life-h{display:flex;gap:10px;align-items:baseline;margin-bottom:6px}
.rv-bar{display:flex;height:12px;border-radius:6px;overflow:hidden;background:#eef1f4}.rv-bar i{display:block}.rv-bar.sm{height:9px;flex:1}
.rv-legend{display:flex;flex-wrap:wrap;gap:14px;margin-top:6px;font-size:12px;color:#43525e}.rv-legend i{display:inline-block;width:10px;height:10px;border-radius:2px;margin-right:4px;vertical-align:-1px}
.rv-two{display:grid;grid-template-columns:1fr 1fr;gap:12px}@media(max-width:1000px){.rv-two{grid-template-columns:1fr}}
.rv-card{background:#fff;border:1px solid #e3e8ee;border-radius:10px;padding:10px 12px}.rv-card h4{margin:0 0 8px;font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:#5b6b78}
.rv-row{display:flex;align-items:center;gap:10px;padding:4px 0}.rv-k{width:60px;font-weight:600}
.rv-top{display:grid;grid-template-columns:1fr auto auto;gap:10px;width:100%;text-align:left;background:none;border:0;border-bottom:1px dashed #e3e8ee;padding:6px 0;cursor:pointer;font:inherit}
.rv-top .t{font-weight:500}.rv-top:hover .t{color:#7500C0}
.rv-tbl{width:100%;border-collapse:collapse;font-size:12.5px}.rv-tbl th{text-align:left;color:#5b6b78;font-weight:600;border-bottom:1px solid #e3e8ee;padding:6px}
.rv-tbl td{border-bottom:1px solid #f0f2f4;padding:6px;vertical-align:top}.rv-tbl tr.click{cursor:pointer}.rv-tbl tr.click:hover{background:#faf6ff}
.rv-tbl.tech{background:#fff;border:1px solid #e3e8ee;border-radius:10px}
.rv-pill{color:#fff;border-radius:10px;padding:1px 8px;font-size:11px;white-space:nowrap}
.rv-q{border:1px solid #cfd7de;border-radius:8px;padding:8px 10px;font:inherit;width:100%}
.rv .mono,.rv-life .mono{font-family:monospace}.rv .mut,.rv-life .mut{color:#6b7a86}.rv .sm{font-size:11.5px}.rv .ok{color:#2f7a4f}.rv .bad{color:#c0392b}
`;
